import React from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  LineChart,
  Target,
  CheckCircle,
  ArrowRight,
  Briefcase,
  PiggyBank,
  BarChart3,
  RefreshCw,
  Shield,
  FileBarChart,
  Layers,
  Users,
  Lightbulb,
  HelpCircle,
  Phone,
  MessageSquare,
  Compass,
  Search,
  Rocket,
  Settings,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';

import RelatedServices from '../components/RelatedServices';
const BusinessAdvisoryUAE = () => {
  usePageSeo({
    title: 'Business Advisory Services in UAE | Financial Consulting',
    description:
      'Expert business advisory services in UAE. Improve performance, manage risk & grow your business with WPAA financial consultants.',
    path: '/business-advisory-uae'
  });

  const services = [
    { title: 'Financial Analysis & Performance Review', description: 'In-depth analysis of your financial health to identify strengths and improvement areas.', icon: LineChart },
    { title: 'Business Growth Strategy & Planning', description: 'Practical growth strategies aligned with your business goals and UAE market dynamics.', icon: Rocket },
    { title: 'Cash Flow Management & Forecasting', description: 'Optimise working capital and forecast cash flow to avoid liquidity shortfalls.', icon: PiggyBank },
    { title: 'Cost Optimization & Profitability', description: 'Identify cost savings and margin improvements without sacrificing quality.', icon: BarChart3 },
    { title: 'Budgeting & Financial Planning', description: 'Annual budgets, scenario planning and financial models that drive accountability.', icon: FileBarChart },
    { title: 'Business Restructuring Advisory', description: 'Strategic restructuring of operations, ownership, or finances to unlock value.', icon: RefreshCw },
    { title: 'Risk Assessment & Internal Controls', description: 'Evaluate business risks and design controls to safeguard assets and data.', icon: Shield },
    { title: 'Management Reporting & Insights', description: 'Custom dashboards and KPI reporting for confident decision-making.', icon: Activity }
  ];

  const whoNeedsAdvisory = [
    { text: 'Growing SMEs looking to scale operations', icon: TrendingUp },
    { text: 'Startups planning expansion', icon: Rocket },
    { text: 'Businesses facing financial challenges', icon: Compass },
    { text: 'Companies preparing for investment or funding', icon: Briefcase },
    { text: 'Owners seeking better financial visibility', icon: Search }
  ];

  const approach = [
    { step: '01', title: 'Understanding Your Business', description: 'Deep-dive into your industry, operations, market position, and goals.' },
    { step: '02', title: 'Financial Assessment', description: 'Comprehensive review of current performance, KPIs, and financial health.' },
    { step: '03', title: 'Identifying Opportunities', description: 'Spot cost savings, revenue growth, and operational improvement areas.' },
    { step: '04', title: 'Strategy Development', description: 'Practical, actionable plans tailored to your resources and timeline.' },
    { step: '05', title: 'Implementation Support', description: 'Hands-on guidance and accountability throughout execution.' },
    { step: '06', title: 'Performance Monitoring', description: 'Regular reviews, course corrections, and continuous optimisation.' }
  ];

  const importance = [
    'Adapt to Corporate Tax (9%)',
    'Maintain strong financial discipline',
    'Optimize operations for profitability',
    'Stay compliant with regulatory requirements'
  ];

  const whyChooseUs = [
    'Experienced financial advisors',
    'UAE market expertise',
    'Data-driven insights',
    'Practical and actionable solutions',
    'Integrated Audit | Tax | Advisory support'
  ];

  const faqs = [
    { q: 'What is business advisory?', a: 'Business advisory involves providing expert guidance on finance, strategy and operations to improve a company\'s performance, profitability and long-term direction.' },
    { q: 'Is advisory useful for small businesses?', a: 'Absolutely. SMEs benefit greatly from advisory services — especially those looking to grow sustainably, control costs, and improve profitability without building a full in-house finance team.' },
    { q: 'Do you provide ongoing advisory support?', a: 'Yes. We offer continuous monthly or quarterly advisory engagements, including KPI reviews, cash-flow monitoring and strategy course-corrections based on your evolving needs.' },
    { q: 'How is advisory different from accounting?', a: 'Accounting focuses on recording past transactions and ensuring compliance. Advisory uses that data to drive forward-looking decisions, strategy and growth — turning historical numbers into competitive advantage.' }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd
        id="ld-ba-service"
        data={serviceSchema({
          name: 'Business Advisory Services in UAE',
          description:
            'Strategic financial guidance — performance review, growth strategy, cash-flow management, cost optimisation, budgeting, restructuring and risk advisory for UAE businesses.',
          path: '/business-advisory-uae',
          serviceType: 'Business Advisory Services'
        })}
      />
      <JsonLd
        id="ld-ba-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Business Advisory UAE', path: '/business-advisory-uae' }
        ])}
      />
      <JsonLd id="ld-ba-faq" data={faqSchema(faqs)} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-emerald-900 via-emerald-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-300 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Lightbulb className="text-emerald-400" size={32} />
              <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Business Advisory</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Business Advisory Services in UAE
            </h1>
            <p className="text-2xl text-emerald-200 mb-6 font-semibold">
              Strategic Financial Guidance for Growing Businesses
            </p>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed max-w-3xl">
              Making the right financial and strategic decisions is critical for business growth. At White Paper Accounts Auditing (WPAA), we provide expert business advisory services in UAE to help companies improve performance, manage risks, and achieve long-term success.
            </p>
            <p className="text-lg text-gray-400 mb-8 max-w-3xl">
              Our advisory solutions are designed to support startups, SMEs, and established businesses with practical, data-driven insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=business-advisory">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg" data-testid="ba-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:+971501219341" data-testid="ba-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Expertise</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Business Advisory Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A wide range of advisory services tailored to your business needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-emerald-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-emerald-600" size={28} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Needs Advisory */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Target Clients</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Who Needs Business Advisory Services?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {whoNeedsAdvisory.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all">
                  <CardContent className="p-6 flex items-start space-x-4">
                    <div className="bg-emerald-100 w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="text-emerald-600" size={24} />
                    </div>
                    <p className="text-gray-700 font-medium pt-2">{item.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Our Advisory Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach to deliver measurable results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {approach.map((item, index) => (
              <div key={index} className="relative bg-gray-50 rounded-2xl p-6 hover:bg-emerald-50 transition-colors">
                <div className="absolute -top-4 -left-4 bg-emerald-600 text-white w-14 h-14 rounded-xl flex items-center justify-center font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mt-4 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Business Advisory is Important in UAE */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-50 to-white border-y-4 border-emerald-400">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Why It Matters</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Why Business Advisory is Important in UAE</h2>
              <p className="text-lg text-gray-700 mb-6">
                With evolving regulations and competitive markets, businesses in the UAE must:
              </p>
              <div className="space-y-4">
                {importance.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="text-emerald-600 flex-shrink-0" size={24} />
                    <p className="text-lg text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-emerald-100 border-l-4 border-emerald-500 p-4 rounded-r-lg">
                <p className="text-emerald-900 font-medium">
                  Our advisory services help you stay ahead in a dynamic business environment.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-8 text-white">
              <Users className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Why Choose WPAA?</h3>
              <div className="space-y-3 mb-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckCircle size={18} className="mt-1 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-emerald-500">
                <div>
                  <p className="text-3xl font-bold">18+</p>
                  <p className="text-sm text-emerald-200">Years Experience</p>
                </div>
                <div>
                  <p className="text-3xl font-bold">200+</p>
                  <p className="text-sm text-emerald-200">Clients Served</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Frequently Asked Questions</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-emerald-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

            <RelatedServices
        services={[
          { title: 'SME Business Package', description: 'Bundled accounting, tax, and advisory support priced for growing SMEs.', path: '/sme-business-services-uae', accent: 'amber' },
          { title: 'Accounting Services', description: 'Accurate records that power confident strategic decisions.', path: '/accounting-services-uae', accent: 'indigo' },
          { title: 'Corporate Tax Services', description: 'Tax-efficient business structuring and ongoing CT advisory.', path: '/corporate-tax-uae', accent: 'teal' }
        ]}
      />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Get Expert Advice for Your Business</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Make smarter decisions with the right financial insights.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact?service=business-advisory">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="ba-cta-consultation-btn">
                Book Your FREE Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="ba-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-emerald-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="ba-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="ba-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BusinessAdvisoryUAE;
